import { useEffect, useRef, useState } from 'react';

import {
  INITIAL_VOICE_GATE,
  PITCH_CONFIDENCE_MIN,
  native,
  stepVoiceGate,
  useRafThrottle,
} from '@muzly/shared';
import type { NativeF0Frame, VoiceGateState } from '@muzly/shared';
import { clampNum } from '@muzly/voice-engine';
import { freqToMidi } from '@muzly/voice-engine';

/**
 * RealtimePitchGraph — 컨셉 muzly-web "음정표"의 라이브 버전.
 *
 * pitchTrackerEvent를 **세션 전체 기록**으로 모아(상한 5분/메모리 cap, 초과 시 앞쪽만
 * 폐기) 수기 SVG로 음정 곡선을 그린다. 화면엔 가로 ~8초 뷰포트만 보이고:
 *   - y축: 음높이(midi/semitone). 보이는 목소리 범위에 맞춰 자동 확대·축소(부드럽게
 *     추적), 반음 격자 + C 라벨. 좁은 범위라야 반음 단위 움직임이 눈에 보인다.
 *   - x축: 시간축(mm:ss, 세션 시작 기준; 폭에 맞춰 1s/5s 적응형 눈금)
 *   - 곡선 색 = 음높이(저음 빨강 → 고음 보라), 곡선 끝에 현재 음 마커(노트명 + ¢),
 *     헤더에 세션 음역(Range), 하단에 rms Energy 스트립 — 레퍼런스: 보컬 레인지 영상 UI.
 *   - voiced 프레임은 연결된 선, unvoiced(f0=null)는 선 끊김. "voiced"는 엔진 판정이
 *     아니라 `stepVoiceGate`가 **방 대비**로 다시 판정한 것 — 엔진의 절대 dBFS 바닥은
 *     암소음을 못 막는다(voice-gate.ts 머리말의 실측 표).
 *   - confidence 낮은 구간은 흐리게
 *   - 라이브 중엔 최신 따라가기(auto-follow), 좌우 드래그로 과거 스크럽(드래그 시 해제)
 *     → "● LIVE" 버튼으로 최신 복귀
 *
 * 47fps 입력을 매 프레임 setState 하지 않는다: 프레임은 ref 링버퍼에 모으고
 * pruneHistory로 상한 밖을 폐기, 렌더는 rAF로 ~30fps throttle, 그릴 땐 뷰포트 구간만 슬라이스.
 */

const CAP_MS = 1_200_000; // 세션 기록 상한 20분(스크럽 가능 범위)
const MAX_POINTS = 60_000; // 메모리 cap(상한 초과 시 앞쪽 폐기) ~20분@47fps
const VIEW_MS = 8000; // 뷰포트 가로 폭(기본 ~8초)
const MIDI_MIN = 36; // C2
const MIDI_MAX = 84; // C6
const VIEW_W = 600;
const VIEW_H = 240;
const AXIS_H = 16; // 하단 시간축 라벨 영역
const PLOT_H = VIEW_H - AXIS_H;
const DRAG_THRESHOLD_PX = 3; // 이 미만 이동은 탭으로 간주(auto-follow 유지)

/** 윈도우에 모으는 최소 좌표 — midi(null=unvoiced) + confidence(흐림 매핑용). */
export type PitchGraphPoint = {
  t: number;
  midi: number | null;
  confidence: number;
  /** 프레임 레벨(dBFS) — 하단 Energy 스트립용. mock 브리지는 없을 수 있다. */
  rmsDb?: number;
};

/** 시간축 눈금 한 개 — 절대 t, mm:ss 라벨, 뷰포트 내 x좌표. */
export type TimeTick = {
  t: number;
  label: string;
  x: number;
};

/** now 기준 상한(기본 5분/2만 점) 밖 포인트를 앞쪽부터 폐기(메모리 상한). */
export const pruneHistory = (
  points: PitchGraphPoint[],
  now: number,
  capMs = CAP_MS,
  maxPoints = MAX_POINTS,
): PitchGraphPoint[] => {
  const byAge = points.filter((p) => now - p.t <= capMs);
  return byAge.length > maxPoints
    ? byAge.slice(byAge.length - maxPoints)
    : byAge;
};

// 이 신뢰도 미만 프레임은 그리지 않음(gap 처리) — unvoiced 자음/무음뿐 아니라
// 옥타브 점프·노이즈로 voiced=true지만 신뢰 낮은 프레임을 곡선에서 제외해 깨끗하게.
// 엔진 게이트와 같은 선을 써야 "엔진은 유성이라는데 그래프는 끊긴다"가 안 생긴다.
const CONF_MIN = PITCH_CONFIDENCE_MIN;

/**
 * voiced & 신뢰도 충분한 포인트만 연속 구간으로 분할 —
 * unvoiced(midi=null) 또는 confidence<confMin 에서 선이 끊긴다.
 */
export const buildSegments = (
  points: PitchGraphPoint[],
  confMin = CONF_MIN,
): PitchGraphPoint[][] => {
  const segments: PitchGraphPoint[][] = [];
  let current: PitchGraphPoint[] = [];
  for (const p of points) {
    if (p.midi == null || p.confidence < confMin) {
      if (current.length) {
        segments.push(current);
      }
      current = [];
      continue;
    }
    current.push(p);
  }
  if (current.length) {
    segments.push(current);
  }
  return segments;
};

/**
 * 옥타브 글리치 제거용 median(3) 스무딩 — 양쪽 이웃이 모두 voiced인 내부 포인트의
 * midi를 (prev, self, next) 중앙값으로 치환해 단일 프레임짜리 옥타브 점프를 흡수한다.
 * 끝점·gap 인접·unvoiced(midi=null)는 그대로 둬 선 끊김을 보존한다(짝수 윈도우의
 * 중앙값 모호성으로 글리치가 되레 증폭되지 않도록 완전한 3-윈도우에서만 동작).
 */
export const medianSmoothMidi = (
  points: PitchGraphPoint[],
): PitchGraphPoint[] =>
  points.map((p, i) => {
    const prev = points[i - 1];
    const next = points[i + 1];
    if (p.midi == null || prev?.midi == null || next?.midi == null) {
      return p;
    }
    const [, median] = [prev.midi, p.midi, next.midi].sort((a, b) => a - b);
    return median === p.midi ? p : { ...p, midi: median };
  });

/** 이보다 크게 벗어나면 스파이크 후보(반음). 비브라토·스쿱은 프레임 간 이 폭을 안 넘는다. */
const SPIKE_DEV_SEMITONES = 5;

/**
 * 벗어난 런이 이 프레임 수(~210ms)를 넘기면 진짜 전환으로 수용한다 — **옥타브가 아닌**
 * 이탈에 한해서. 옥타브 이탈은 아래 `SPIKE_OCTAVE_HOLD_FRAMES`를 쓴다.
 */
const SPIKE_HOLD_FRAMES = 10;

/** 편차가 정확한 옥타브 배수에서 이만큼 안이면 검출기 옥타브 오류로 본다(반음). */
const SPIKE_OCTAVE_TOL_SEMITONES = 1.5;

/**
 * 옥타브 이탈이 진짜로 인정받으려면 버텨야 하는 프레임 수(~640ms).
 *
 * 지속 시간만으로는 못 가른다는 것이 계측 결과다. 실기기 raw 3테이크를 지금의
 * post-processor에 다시 물려 화면 파이프라인까지 통과시킨 뒤 이탈 런을 세면:
 *
 * | 런 길이 | 1 | 3 | 7 | 20 | 76 |
 * |---|---|---|---|---|---|
 * | 평균 편차(반음) | +12.1 | -12.4 | -11.6 | +10.5 | -29.8 |
 * | 성격 | 옥타브 | 옥타브 | 옥타브 | 옥타브 | 비옥타브 |
 *
 * 스파이크는 길이가 1~20프레임으로 흩어져 있고 **전부 옥타브 편차**다. 210ms 문턱 하나로는
 * 20프레임(430ms)짜리를 못 잡고, 문턱을 그만큼 올리면 진짜 도약이 다 늦어진다. 가르는 축은
 * 시간이 아니라 편차가 옥타브냐다 — 엔진 post-processor가 같은 근거를 쓴다.
 *
 * 640ms는 계측된 최장 옥타브 스파이크(430ms)에 여유를 둔 값이다. 대가: 진짜 옥타브 도약은
 * 640ms를 버텨야 그려진다. 이 화면이 재는 것(지속 모음·글리산도)에서 순간 옥타브 도약은
 * 나오지 않는다 — 글리산도는 프레임 간 변화가 작아 애초에 걸리지 않는다.
 */
const SPIKE_OCTAVE_HOLD_FRAMES = 30;

const isOctaveIsh = (deviation: number): boolean => {
  const size = Math.abs(deviation);
  return (
    Math.abs(size - 12) <= SPIKE_OCTAVE_TOL_SEMITONES ||
    Math.abs(size - 24) <= SPIKE_OCTAVE_TOL_SEMITONES
  );
};

/** 이 편차를 진짜로 받아주기까지 요구하는 프레임 수. */
const holdFramesFor = (deviation: number): number =>
  isOctaveIsh(deviation) ? SPIKE_OCTAVE_HOLD_FRAMES : SPIKE_HOLD_FRAMES;

/**
 * 옥타브 오류로 판정된 값을 기준음 옥타브로 되접는다. 옥타브 배수가 아니면 null —
 * 그때는 접을 근거가 없으니 그리지 않는다.
 *
 * 지우기만 하면 스파이크 자리에 그 길이만큼 구멍이 남는다(계측: 실기기 픽스처에서
 * 219점 중 66점). 사용자가 바로 앞서 고친 증상이 "곡선이 끊긴다"였으므로 스파이크를
 * 구멍으로 바꾸는 것은 절반만 고치는 것이다. 편차가 정확히 옥타브라는 것을 이미 확인한
 * 뒤이므로 되접은 값은 기준음의 ±1.5반음 안에 떨어진다 — `post_processor.hpp`의
 * `correct_octave`가 엔진 안에서 같은 보정을 한다.
 */
const foldToRef = (midi: number, ref: number): number | null => {
  const deviation = midi - ref;
  const octaves = Math.round(Math.abs(deviation) / 12);
  if (octaves < 1 || octaves > 2) {
    return null;
  }
  if (
    Math.abs(Math.abs(deviation) - octaves * 12) > SPIKE_OCTAVE_TOL_SEMITONES
  ) {
    return null;
  }
  return midi - Math.sign(deviation) * octaves * 12;
};

/** 기준음이 이보다 오래 침묵하면 버린다 — 긴 쉼 뒤 새 음을 스파이크로 오판하지 않게. */
const SPIKE_REF_RESET_MS = 800;

/**
 * 기준음을 세울 때 보는 점 수. 하나로 세우면 그 하나가 스파이크일 때 필터가 뒤집혀
 * **진짜 음이 지워진다** — 뷰포트 왼쪽 끝은 매 렌더 다른 프레임에서 시작하므로 언젠가
 * 반드시 스파이크에서 시작한다. 중앙값이면 3개 중 1개가 스파이크여도 안 움직인다
 * (`voice-gate.ts`가 워밍업 프레임에 같은 방어를 한다).
 */
const SPIKE_SEED_FRAMES = 3;

const isDrawable = (p: PitchGraphPoint): boolean =>
  p.midi != null && p.confidence >= CONF_MIN;

/** from부터 그릴 수 있는 점 최대 `SPIKE_SEED_FRAMES`개의 중앙값 — 기준음 시드. */
const seedRef = (points: PitchGraphPoint[], from: number): number => {
  const window: number[] = [];
  for (
    let k = from;
    k < points.length && window.length < SPIKE_SEED_FRAMES;
    k += 1
  ) {
    if (isDrawable(points[k])) {
      window.push(points[k].midi as number);
    }
  }
  window.sort((a, b) => a - b);
  return window[Math.floor(window.length / 2)];
};

/**
 * 표시 전용 스파이크 억제 — 마지막 안정 음에서 `SPIKE_DEV_SEMITONES` 이상 벗어난 런이
 * 수용 한도 안에 제자리로 돌아오면(또는 그 전에 무성으로 끊기면) 그리지 않는다. 한도는
 * 편차가 옥타브 배수인지에 따라 다르다(`holdFramesFor`) — 스파이크는 길이가 흩어져
 * 있지만 전부 옥타브 편차라는 계측 결과를 따른다.
 *
 * 엔진 post-processor가 잔여 옥타브 점프를 테이크당 1~2개 남기고(fry 구간 — 시간축으로도
 * confidence로도 진짜와 못 가른다, `post_processor.hpp`), median(3)은 1프레임짜리만
 * 흡수한다. 화면에 남던 좁은 V자 스파이크가 이 사이에 있다. 여기는 화면이라 잘못 지워도
 * 210ms 뒤 복구되지만, 스코어링은 raw를 쓰므로 건드리지 않는다(GOAL 불변식).
 *
 * 오류로 판정한 프레임은 기준음 옥타브로 **되접어** 그린다(`foldToRef`) — 편차가 정확히
 * 옥타브임을 확인한 뒤이므로 접은 값은 기준음의 ±1.5반음 안이다. 접을 근거가 없는(옥타브
 * 배수가 아닌) 편차만 midi=null로 두어 선을 끊는다. 없는 값을 지어내지는 않는다.
 *
 * **채택 직전 기준음을 기억한다**(`prevRef`). 한도를 넘겨 채택된 이탈에서 목소리가 원래
 * 음으로 돌아올 때, 그 복귀는 새 기준에서 보면 또 이탈이라 두 번째 구멍이 뚫린다 —
 * 스파이크 하나를 지우려다 그 뒤 210ms를 마저 지우는 셈이다. 원래 음으로의 복귀는
 * 런을 세지 않고 즉시 받는다.
 *
 * 실기기 픽스처(`deviceVowelFrames.csv`, 게이트+median(3) 통과분): 점프 11 → 3,
 * 그려지는 점 219 → 196.
 */
export const suppressOctaveSpikes = (
  points: PitchGraphPoint[],
): PitchGraphPoint[] => {
  const out = points.slice();
  let ref: number | null = null;
  let prevRef: number | null = null;
  let refT = 0;
  let i = 0;
  const near = (midi: number, r: number | null): boolean =>
    r != null && Math.abs(midi - r) <= SPIKE_DEV_SEMITONES;

  while (i < out.length) {
    const p = out[i];
    if (!isDrawable(p)) {
      i += 1;
      continue;
    }
    const midi = p.midi as number;
    if (ref != null && p.t - refT > SPIKE_REF_RESET_MS) {
      ref = null;
      prevRef = null;
    }
    if (ref == null) {
      ref = seedRef(out, i);
    }
    if (near(midi, ref) || near(midi, prevRef)) {
      // 두 번째 조건 = 채택했던 이탈에서 원래 음으로의 복귀. 즉시 받는다.
      if (near(midi, prevRef)) {
        prevRef = null;
      }
      ref = midi;
      refT = p.t;
      i += 1;
      continue;
    }
    // 벗어난 런 — 제자리 복귀 전(또는 무성 단절·수용 한도 전)까지의 길이를 잰다.
    const hold = holdFramesFor(midi - ref);
    let runEnd = i;
    let returned = false;
    while (runEnd < out.length && runEnd - i < hold) {
      const q = out[runEnd];
      if (!isDrawable(q)) {
        break; // 무성으로 끊긴 짧은 런 = 스파이크
      }
      if (near(q.midi as number, ref)) {
        returned = true;
        break;
      }
      runEnd += 1;
    }
    if (runEnd - i >= hold && !returned) {
      prevRef = ref; // 지속 = 진짜 전환. 원래 음은 기억해 두고 새 기준을 받는다.
      ref = midi;
      refT = p.t;
      i += 1;
      continue;
    }
    for (let k = i; k < runEnd; k += 1) {
      out[k] = { ...out[k], midi: foldToRef(out[k].midi as number, ref) };
    }
    i = runEnd; // p 자신이 런의 첫 점이므로 runEnd > i가 보장된다
  }
  return out;
};

/**
 * 뷰포트 우측 끝(viewEnd)을 [sessionStart+viewMs, latest]로 클램프 —
 * 좌측 끝이 세션 시작 이전으로 가지 않게 하고, 최신을 넘어가지 않게 한다.
 * 세션이 viewMs보다 짧으면 minEnd=latest라 스크럽 불가(항상 최신).
 */
export const clampViewEnd = (
  viewEnd: number,
  sessionStart: number,
  latest: number,
  viewMs = VIEW_MS,
): number => {
  const minEnd = Math.min(latest, sessionStart + viewMs);
  return Math.min(latest, Math.max(minEnd, viewEnd));
};

/** 절대 t → 뷰포트 내 x좌표(viewStart=0, viewEnd=viewW). */
export const xInView = (
  t: number,
  viewStart: number,
  viewMs = VIEW_MS,
  viewW = VIEW_W,
): number => (viewW * (t - viewStart)) / viewMs;

/**
 * 뷰포트 구간 포인트만 추출(앞뒤 한 점씩 포함해 선이 가장자리까지 이어지게).
 * 세션 전체(최대 2만 점)를 매 프레임 폴리라인으로 그리지 않기 위한 슬라이스.
 */
export const sliceToView = (
  points: PitchGraphPoint[],
  viewStart: number,
  viewEnd: number,
  lookback = 1,
): PitchGraphPoint[] => {
  let lo = points.length;
  let hi = -1;
  for (let i = 0; i < points.length; i += 1) {
    const { t } = points[i];
    if (t >= viewStart && t <= viewEnd) {
      if (i < lo) {
        lo = i;
      }
      hi = i;
    }
  }
  if (hi < 0) {
    return [];
  }
  return points.slice(
    Math.max(0, lo - lookback),
    Math.min(points.length - 1, hi + 1) + 1,
  );
};

/**
 * 억제 판정용 여유분(왼쪽)을 잘라내고 그릴 구간만 남긴다 — 선이 왼쪽 가장자리까지
 * 이어지도록 `viewStart` 직전 한 점은 남긴다.
 */
export const trimToView = (
  points: PitchGraphPoint[],
  viewStart: number,
): PitchGraphPoint[] => {
  const firstInside = points.findIndex((p) => p.t >= viewStart);
  return firstInside <= 0 ? points : points.slice(firstInside - 1);
};

const TICK_INTERVALS_MS = [
  1000, 5000, 10_000, 30_000, 60_000, 300_000,
] as const;

/** 폭(targetCount)에 맞춰 눈금 간격 선택 — 데스크탑 ~1s, 좁은 모바일 ~5s. */
export const chooseTickInterval = (viewMs = VIEW_MS, targetCount = 8): number =>
  TICK_INTERVALS_MS.find((interval) => viewMs / interval <= targetCount) ??
  TICK_INTERVALS_MS[TICK_INTERVALS_MS.length - 1];

/** ms(세션 시작 기준 경과) → mm:ss. */
export const formatClock = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

/** 뷰포트 [viewStart, viewEnd] 안의 시간축 눈금(세션 시작 기준 mm:ss). */
export const buildTimeTicks = (
  viewStart: number,
  viewEnd: number,
  sessionStart: number,
  viewMs = VIEW_MS,
  targetCount = 8,
): TimeTick[] => {
  const interval = chooseTickInterval(viewMs, targetCount);
  const relEnd = viewEnd - sessionStart;
  const firstRel = Math.ceil((viewStart - sessionStart) / interval) * interval;
  const ticks: TimeTick[] = [];
  for (let rel = firstRel; rel <= relEnd; rel += interval) {
    if (rel < 0) {
      continue;
    }
    const t = sessionStart + rel;
    ticks.push({
      t,
      label: formatClock(rel),
      x: xInView(t, viewStart, viewMs),
    });
  }
  return ticks;
};

const NOTE_NAMES = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
] as const;

/** midi(반올림) → "C#4" 노트 이름. */
export const noteLabel = (midi: number): string => {
  const n = Math.round(midi);
  return `${NOTE_NAMES[((n % 12) + 12) % 12]}${Math.floor(n / 12) - 1}`;
};

/** 가장 가까운 반음에서 벗어난 정도(¢, -50..+50). */
export const centsOff = (midi: number): number =>
  Math.round((midi - Math.round(midi)) * 100);

export type MidiRange = { lo: number; hi: number };

/**
 * midi → 트레일 hue. 저음 빨강(0) → 고음 보라(280).
 *
 * **보이는 범위에 걸쳐** 매핑한다. C2~C6 절대 매핑이면 고음역 노래는 곡선 전체가 보라
 * 한 색이 되어 색이 아무 정보도 안 준다 — 색은 "지금 이 화면 안에서 높은가 낮은가"를
 * 보여야 한다.
 */
const trailColor = (midi: number, range: MidiRange): string => {
  const span = Math.max(1, range.hi - range.lo);
  const norm = clampNum((midi - range.lo) / span, 0, 1);
  return `hsl(${Math.round(280 * norm)} 75% 52%)`;
};

const FULL_RANGE: MidiRange = { lo: MIDI_MIN, hi: MIDI_MAX };
const FIT_PAD_SEMITONES = 3;
const FIT_MIN_SPAN = 14;
/**
 * 축을 맞출 때 위아래로 잘라내는 비율. 마이크 핸들링 잡음이나 남은 옥타브 오류 몇 점이
 * 최저·최고가 되면 축이 그만큼 벌어지고 진짜 곡선은 가운데 눌려 납작해진다 — 축은
 * 목소리가 실제로 머문 곳에 맞아야 한다. 최신 점은 아래에서 따로 포함시킨다.
 */
const FIT_TRIM_RATIO = 0.04;

/**
 * 보이는 점들에 맞춘 y축 범위 — 위아래 4%를 잘라낸 뒤 여백 ±3반음, 최소 폭 14반음,
 * C2~C6 안으로 클램프. 그릴 점이 없으면 null(호출자가 이전 범위를 유지한다).
 *
 * 고정 C2~C6(반음당 ~5px)에서는 반음 격자도 미세한 음정 움직임도 안 보인다 — 범위를
 * 목소리에 맞춰야 둘 다 산다.
 */
export const fitMidiRange = (points: PitchGraphPoint[]): MidiRange | null => {
  const midis = points
    .filter(isDrawable)
    .map((p) => p.midi as number)
    .sort((a, b) => a - b);
  if (!midis.length) {
    return null;
  }
  const trim = Math.floor(midis.length * FIT_TRIM_RATIO);
  const newest = points[points.length - 1];
  // 잘라낸 뒤에도 지금 그려지는 값(라이브 마커)은 반드시 화면 안에 있어야 한다.
  const latest = isDrawable(newest) ? (newest.midi as number) : midis[trim];
  let lo = Math.min(midis[trim], latest) - FIT_PAD_SEMITONES;
  let hi = Math.max(midis[midis.length - 1 - trim], latest) + FIT_PAD_SEMITONES;
  const shortfall = FIT_MIN_SPAN - (hi - lo);
  if (shortfall > 0) {
    lo -= shortfall / 2;
    hi += shortfall / 2;
  }
  return { lo: Math.max(MIDI_MIN, lo), hi: Math.min(MIDI_MAX, hi) };
};

/** 격자 간격(반음). 범위가 넓으면 선을 솎아 격자가 소음이 되지 않게 한다. */
const gridStepFor = (span: number): number =>
  span <= 30 ? 1 : span <= 60 ? 2 : 3;

const yOf = (midi: number, range: MidiRange): number => {
  const clamped = Math.min(range.hi, Math.max(range.lo, midi));
  return PLOT_H * (1 - (clamped - range.lo) / (range.hi - range.lo));
};

/** confidence(0~1)를 stroke-opacity(0.25~1)로 — 낮을수록 흐리게. */
const opacityOf = (confidence: number): number =>
  0.25 + 0.75 * clampNum(confidence, 0, 1);

const pointsAttr = (
  segment: PitchGraphPoint[],
  mapX: (t: number) => number,
  range: MidiRange,
): string =>
  segment
    .map(
      (p) =>
        `${mapX(p.t).toFixed(1)},${yOf(p.midi as number, range).toFixed(1)}`,
    )
    .join(' ');

/** 세그먼트 평균 confidence(0~1) — 선/점 흐림(opacity) 매핑에 쓴다. */
export const avgConfidence = (segment: PitchGraphPoint[]): number =>
  segment.reduce((sum, p) => sum + p.confidence, 0) / segment.length;

/** 세그먼트 평균 midi — 청크 색상 매핑용. */
const avgMidi = (segment: PitchGraphPoint[]): number =>
  segment.reduce((sum, p) => sum + (p.midi as number), 0) / segment.length;

/**
 * 음고에 따라 색이 변하는 트레일을 위해 세그먼트를 겹치는 청크로 나눈다(끝점 1개 공유
 * → 선이 이어져 보인다). 점마다 <line>을 쓰면 뷰포트당 ~400 엘리먼트 × 30fps라 비싸고,
 * 세그먼트당 한 색이면 긴 지속음에서 색 변화가 죽는다 — 청크(8점 ≈ 170ms)가 절충이다.
 */
export const chunkForColor = (
  segment: PitchGraphPoint[],
  size = 8,
): PitchGraphPoint[][] => {
  if (segment.length <= size) {
    return [segment];
  }
  const chunks: PitchGraphPoint[][] = [];
  for (let i = 0; i < segment.length - 1; i += size - 1) {
    chunks.push(segment.slice(i, i + size));
  }
  return chunks;
};

/** Energy 스트립 버킷 — 프레임 몇 개를 rms 평균 막대 하나로. */
export type EnergyBucket = {
  t0: number;
  t1: number;
  rmsDb: number;
  /** 버킷 안에 그려진(유성) midi가 있으면 그 평균 — 막대 색을 트레일과 맞춘다. */
  midi: number | null;
};

export const bucketEnergy = (
  points: PitchGraphPoint[],
  size = 4,
): EnergyBucket[] => {
  const buckets: EnergyBucket[] = [];
  for (let i = 0; i < points.length; i += size) {
    const slice = points.slice(i, i + size);
    const withRms = slice.filter((p) => Number.isFinite(p.rmsDb));
    if (!withRms.length) {
      continue;
    }
    const voiced = slice.filter(
      (p) => p.midi != null && p.confidence >= CONF_MIN,
    );
    buckets.push({
      t0: slice[0].t,
      t1: slice[slice.length - 1].t,
      rmsDb:
        withRms.reduce((sum, p) => sum + (p.rmsDb as number), 0) /
        withRms.length,
      midi: voiced.length ? avgMidi(voiced) : null,
    });
  }
  return buckets;
};

const ENERGY_H = 32;
const RMS_FLOOR_DB = -60;

/** rms(dBFS) → 막대 높이(0~ENERGY_H). -60dB 아래는 0. */
const energyHeight = (rmsDb: number): number =>
  ENERGY_H * clampNum((rmsDb - RMS_FLOOR_DB) / -RMS_FLOOR_DB, 0, 1);

/**
 * 프레임 하나를 그래프가 그릴 포인트로 바꾼다. 그리지 않기로 한 프레임은 midi=null이라
 * `buildSegments`에서 선이 끊긴다.
 *
 * 엔진의 무음 바닥(절대 dBFS)은 암소음을 못 막는다 — 근거와 실측 표는 `voice-gate.ts`
 * 머리말에 있다. 이 화면이 오래 방을 그린 이유는 그 상대 게이트가 온보딩 캡처에만 걸려
 * 있었다는 것뿐이다.
 *
 * 훅 안에 두지 않고 꺼내 둔 이유: 훅 안에 있으면 게이트를 태웠는지를 테스트가 확인할 수
 * 없다(게이트를 따로 호출하는 테스트는 게이트를 빼도 초록이다). `roomGate.spec.ts`가
 * 실제 엔진 프레임으로 이 함수를 돌린다.
 */
/** Hz → MIDI(연속값). 표시 전용 — 스코어링은 raw f0Hz를 쓴다(GOAL.md 불변식). */
const hzToMidi = freqToMidi;

/**
 * 그릴 음높이. 네이티브가 스무딩 값을 주면 **그것을 쓴다**.
 *
 * 이 화면이 오래 톱니였던 이유가 여기다. raw f0는 프레임 단위로 옥타브를 갈아탄다 —
 * 한 창 안에는 "이 주기가 진짜 음인가"를 가르는 정보가 없기 때문이고, 그래서 엔진은
 * 시간축에서 그것을 누르는 post-processor를 따로 들고 있다(`post_processor.hpp`).
 * 이 컴포넌트만 그 값을 안 읽고 raw를 그렸다.
 *
 * 실기기 3개 테이크에서 잰 옥타브 점프 수 — 같은 프레임, 같은 세션:
 *
 * | 테이크 | raw | smoothed |
 * |---|---|---|
 * | 1 | 37 | **4** |
 * | 2 | 47 | **8** |
 * | 3 | 47 | **19** |
 *
 * 온보딩 곡선(`livePoints.ts`)이 같은 값을 그린다 — 그쪽 곡선이 멀쩡했고 이쪽만
 * 톱니였던 것이 이 차이였다. 정본이 여기인 이유는 import 방향이다: `livePoints`가
 * 이미 이 배럴에서 `pruneHistory`를 가져온다(components → features는 반대 방향).
 *
 * `smoothingActive`가 `smoothedF0Hz == null`의 **두 가지 뜻**을 가른다. 거짓이면
 * 엔진이 애초에 스무딩을 안 보내는 것이므로(스무딩 off·mock 브리지) raw로 떨어진다 —
 * 안 그러면 브라우저 개발 경로 곡선이 통째로 사라진다. 참이면 엔진이 이 테이크에서
 * 값을 보내는데 **이 프레임만 보류한 것**이고, 그건 post-processor가 아직 트랙을
 * 못 잡았다고 답한 프레임이다(`post_processor.hpp` `!engaged_ || hist_count_ == 0`).
 * 거기서 raw를 그리면 엔진 판단을 뒤집는다 — 기기 2026-09-18 로그의 온셋 한 프레임짜리
 * 튐(raw 37.0·44.3·56.3, 테이크는 45~60)이 전부 이 경로였다. 그릴 것이 없다고 답한다.
 *
 * 호출자는 `useVoiceCapture`의 `smoothedSeen`(테이크 래치)을 넘긴다 — "엔진이 이
 * 테이크에서 스무딩을 한 번이라도 실어 왔나"의 기존 답이고, 두 번째 개념을 만들면
 * 갈라진다.
 */
export const displayMidi = (
  frame: NativeF0Frame,
  smoothingActive: boolean,
): number | null => {
  if (frame.smoothedF0Hz != null) {
    return hzToMidi(frame.smoothedF0Hz);
  }
  return smoothingActive ? null : frame.midi;
};

export const gateFrameToPoint = (
  state: VoiceGateState,
  frame: NativeF0Frame,
  smoothingActive: boolean,
): { state: VoiceGateState; point: PitchGraphPoint } => {
  const gated = stepVoiceGate(state, frame);
  // rmsDb가 없으면 방을 잴 수 없고, 그때 stepVoiceGate는 전부 무성으로 답한다 — 그대로
  // 쓰면 곡선이 통째로 사라져 원래 버그보다 나쁘다. 잴 수 없을 때는 게이트 이전
  // 동작(confidence만 보고 그리기)으로 떨어진다.
  const drawable = Number.isFinite(frame.rmsDb) ? gated.isVoice : true;
  return {
    state: gated.state,
    point: {
      t: frame.timestampMs,
      midi: drawable ? displayMidi(frame, smoothingActive) : null,
      confidence: frame.confidence,
      rmsDb: frame.rmsDb,
    },
  };
};

export type SpikeStream = {
  ref: number | null;
  prevRef: number | null;
  refT: number;
  pending: number;
  /** 지금 대기 중인 이탈이 수용되기까지 필요한 프레임 수(런 시작 때 정해진다). */
  pendingHold: number;
  /** 기준음이 없을 때 세우는 중인 후보 음높이. 아직 없으면 null. */
  seed: number | null;
  /** 그 후보가 연속으로 버틴 프레임 수. */
  seedFrames: number;
  /** 그 후보를 마지막으로 본 시각 — 띄엄띄엄 온 점이 연속으로 세지 않게. */
  seedT: number;
};

export const INITIAL_SPIKE_STREAM: SpikeStream = {
  ref: null,
  prevRef: null,
  refT: 0,
  pending: 0,
  pendingHold: SPIKE_HOLD_FRAMES,
  seed: null,
  seedFrames: 0,
  seedT: 0,
};

/**
 * 기준음 후보를 "연속"으로 볼 수 있는 최대 프레임 간격(ms). 프레임 주기가 ≈21ms이므로
 * 몇 프레임 빠지는 것까지는 같은 런으로 본다.
 *
 * 이 상한이 없으면 방 누수처럼 **띄엄띄엄 오지만 음높이는 늘 같은**(암소음 ≈75Hz) 점들이
 * 시드 카운터를 누적시켜 결국 기준음을 세운다 — 버티기를 요구한 이유가 사라진다.
 */
const SPIKE_SEED_GAP_MS = 100;

/**
 * `suppressOctaveSpikes`의 스트리밍 판정 — Range 헤더용. 점 하나를 먹이고 "수용됐나"를
 * 돌려준다. 세션 전체 버퍼(최대 6만 점)를 렌더마다 다시 필터링하기엔 커서, 여기서 한
 * 점씩 판정해 min/max만 갱신한다.
 *
 * 배치판과 두 가지가 다르고 둘 다 의도된 것이다. 앞을 못 보므로 (a) 기준음을 중앙값으로
 * 세울 수 없고 `SPIKE_HOLD_FRAMES`만큼 버틴 값으로 세우며, (b) 진짜 전환의 앞
 * `SPIKE_HOLD_FRAMES-1` 프레임을 음역에서 뺀다. Range는 최소·최대라서 뒤따르는 지속
 * 구간이 같은 음역을 덮는다 — 대신 스파이크가 음역을 영구히 벌려놓는 일이 없다(그게 이
 * 판정을 넣은 이유다).
 *
 * **기준음을 세울 때도 버티기를 요구한다.** 첫 점을 그대로 기준음으로 쓰면 고립된 점
 * 하나가 항상 음역에 들어간다 — 기준음이 없는 순간(세션 시작, 그리고 `SPIKE_REF_RESET_MS`
 * 침묵 뒤)에 도착한 점은 비교할 상대가 없어 무조건 수용되기 때문이다. 실기기 확인에서
 * 조용한 사무실(-46.9~-50.4dBFS)이 엔진의 절대 무음선(-45)을 잠깐 넘긴 프레임 몇 개가
 * 그렇게 들어와, 실제 발성이 B2~D3인 테이크의 헤더가 C#2까지 벌어졌다. 이탈을 수용할 때
 * 요구하는 것과 같은 지속(`SPIKE_HOLD_FRAMES` ≈ 210ms)을 기준음에도 요구하면 그 점들은
 * 못 들어온다. 진짜 음은 그보다 오래 이어지므로 헤더가 210ms 늦게 잡힐 뿐이다.
 */
export const stepSpikeStream = (
  s: SpikeStream,
  midi: number,
  t: number,
): boolean => {
  if (s.ref != null && t - s.refT > SPIKE_REF_RESET_MS) {
    s.ref = null;
    s.prevRef = null;
    s.seed = null;
    s.seedFrames = 0;
  }
  const near = (r: number | null): boolean =>
    r != null && Math.abs(midi - r) <= SPIKE_DEV_SEMITONES;
  if (s.ref == null) {
    const continues =
      s.seed != null && near(s.seed) && t - s.seedT <= SPIKE_SEED_GAP_MS;
    s.seedFrames = continues ? s.seedFrames + 1 : 1;
    s.seed = midi;
    s.seedT = t;
    if (s.seedFrames < SPIKE_HOLD_FRAMES) {
      return false;
    }
    s.ref = midi;
    s.refT = t;
    s.pending = 0;
    s.seed = null;
    s.seedFrames = 0;
    return true;
  }
  if (near(s.ref) || near(s.prevRef)) {
    if (near(s.prevRef)) {
      s.prevRef = null; // 채택했던 이탈에서 원래 음으로 복귀
    }
    s.ref = midi;
    s.refT = t;
    s.pending = 0;
    return true;
  }
  if (s.pending === 0) {
    s.pendingHold = holdFramesFor(midi - s.ref);
  }
  s.pending += 1;
  if (s.pending >= s.pendingHold) {
    s.prevRef = s.ref;
    s.ref = midi;
    s.refT = t;
    s.pending = 0;
    return true;
  }
  return false;
};

/** 링버퍼에 pitchTrackerEvent를 모으고 rAF로 throttle된 스냅샷 state를 돌려준다. */
const usePitchHistory = (): {
  points: PitchGraphPoint[];
  sessionStart: number;
  /** 세션 동안 그려진 최저~최고 midi(cap으로 점이 폐기돼도 유지) — Range 헤더용. */
  sessionRange: MidiRange | null;
} => {
  const bufferRef = useRef<PitchGraphPoint[]>([]);
  const startRef = useRef<number | null>(null); // 세션 첫 프레임 시각(진짜 시작)
  // 이 방의 조용함. 세션마다 새로 잰다 — 방이 바뀌면 기준도 바뀌어야 한다.
  const gateRef = useRef<VoiceGateState>(INITIAL_VOICE_GATE);
  const rangeRef = useRef<MidiRange | null>(null);
  const spikeRef = useRef<SpikeStream>({ ...INITIAL_SPIKE_STREAM });
  // "엔진이 이 세션에서 스무딩을 한 번이라도 실어 왔나" — `displayMidi`가 null의 두 뜻을
  // 가르는 데 쓴다. 온보딩 쪽 정본은 `useVoiceCapture`의 `smoothedSeen`이고, 이 화면은
  // 그 훅을 안 거치고 직접 구독하므로 같은 래치를 여기서 든다(세션마다 초기화).
  const smoothedSeenRef = useRef(false);
  const [points, setPoints] = useState<PitchGraphPoint[]>([]);
  const [sessionStart, setSessionStart] = useState(0);
  const [sessionRange, setSessionRange] = useState<MidiRange | null>(null);

  useEffect(
    () =>
      native.on('pitchTrackerEvent', (event) => {
        if (event.type === 'session_started') {
          bufferRef.current = [];
          startRef.current = null;
          gateRef.current = INITIAL_VOICE_GATE;
          rangeRef.current = null;
          spikeRef.current = { ...INITIAL_SPIKE_STREAM };
          smoothedSeenRef.current = false;
          setSessionStart(0);
          return;
        }
        if (event.type !== 'pitch_frame') {
          return;
        }
        const { timestampMs } = event.frame;
        // 진짜 세션 시작 = 첫 프레임 시각으로 고정(이후 cap으로 앞쪽이 폐기돼도 유지).
        // 시간축 라벨이 "남은 버퍼"가 아니라 "실제 경과시간(mm:ss)"을 표시하게 한다.
        if (startRef.current == null) {
          startRef.current = timestampMs;
          setSessionStart(timestampMs);
        }
        smoothedSeenRef.current =
          smoothedSeenRef.current || event.frame.smoothedF0Hz != null;
        const { state, point } = gateFrameToPoint(
          gateRef.current,
          event.frame,
          smoothedSeenRef.current,
        );
        gateRef.current = state;
        if (
          point.midi != null &&
          point.confidence >= CONF_MIN &&
          stepSpikeStream(spikeRef.current, point.midi, point.t)
        ) {
          const prev = rangeRef.current;
          rangeRef.current = prev
            ? {
                lo: Math.min(prev.lo, point.midi),
                hi: Math.max(prev.hi, point.midi),
              }
            : { lo: point.midi, hi: point.midi };
        }
        bufferRef.current = pruneHistory(
          [...bufferRef.current, point],
          timestampMs,
        );
      }),
    [],
  );

  useRafThrottle(() => {
    setPoints(bufferRef.current);
    setSessionRange(rangeRef.current);
  });

  return { points, sessionStart, sessionRange };
};

interface ViewportPan {
  svgRef: React.RefObject<SVGSVGElement | null>;
  viewEnd: number;
  autoFollow: boolean;
  goLive: () => void;
  pxWidth: number;
  handlers: {
    onPointerDown: (e: React.PointerEvent<SVGSVGElement>) => void;
    onPointerMove: (e: React.PointerEvent<SVGSVGElement>) => void;
    onPointerUp: (e: React.PointerEvent<SVGSVGElement>) => void;
  };
}

/** 뷰포트 팬 — 라이브 따라가기 + 포인터/터치 좌우 드래그 스크럽 + LIVE 복귀. */
const useViewportPan = (latest: number, sessionStart: number): ViewportPan => {
  const [autoFollow, setAutoFollow] = useState(true);
  const [manualEnd, setManualEnd] = useState(0);
  const [pxWidth, setPxWidth] = useState(VIEW_W);
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef({ active: false, startX: 0, startEnd: 0 });

  useEffect(() => {
    const el = svgRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const ro = new ResizeObserver(([entry]) => {
      const w = entry?.contentRect.width;
      if (w) {
        setPxWidth(w);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const viewEnd = autoFollow
    ? latest
    : clampViewEnd(manualEnd, sessionStart, latest);

  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    drag.current = { active: true, startX: e.clientX, startEnd: viewEnd };
    svgRef.current?.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!drag.current.active) {
      return;
    }
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) < DRAG_THRESHOLD_PX) {
      return;
    }
    setAutoFollow(false);
    const width = svgRef.current?.getBoundingClientRect().width || VIEW_W;
    const dt = (dx / width) * VIEW_MS; // 오른쪽 드래그 → 과거로(viewEnd 감소)
    setManualEnd(
      clampViewEnd(drag.current.startEnd - dt, sessionStart, latest),
    );
  };
  const onPointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
    drag.current.active = false;
    svgRef.current?.releasePointerCapture?.(e.pointerId);
  };
  const goLive = () => setAutoFollow(true);

  return {
    svgRef,
    viewEnd,
    autoFollow,
    goLive,
    pxWidth,
    handlers: { onPointerDown, onPointerMove, onPointerUp },
  };
};

/** 반음 격자 + C 라벨. 범위가 넓으면 선을 솎아낸다(C선은 항상 남는다). */
const PitchGridLines = ({ range }: { range: MidiRange }) => {
  const step = gridStepFor(range.hi - range.lo);
  const midis: number[] = [];
  for (let m = Math.ceil(range.lo); m <= Math.floor(range.hi); m += 1) {
    if (m % step === 0 || m % 12 === 0) {
      midis.push(m);
    }
  }
  return (
    <>
      {midis.map((m) => (
        <g key={m}>
          <line
            x1={0}
            y1={yOf(m, range)}
            x2={VIEW_W}
            y2={yOf(m, range)}
            style={{ stroke: 'hsl(var(--border))' }}
            strokeWidth={1}
            opacity={m % 12 === 0 ? 1 : 0.35}
            vectorEffect="non-scaling-stroke"
          />
          {m % 12 === 0 && (
            <text
              x={4}
              y={yOf(m, range) - 3}
              style={{ fill: 'hsl(var(--muted-foreground))' }}
              fontSize={10}
            >
              {noteLabel(m)}
            </text>
          )}
        </g>
      ))}
    </>
  );
};

/** 음고 → 색 트레일. 세그먼트를 청크로 나눠 각 청크를 평균 midi 색으로 긋는다. */
const PitchTrail = ({
  segments,
  mapX,
  range,
}: {
  segments: PitchGraphPoint[][];
  mapX: (t: number) => number;
  range: MidiRange;
}) => (
  <>
    {segments.map((segment, i) =>
      segment.length === 1 ? (
        <circle
          key={i}
          cx={mapX(segment[0].t)}
          cy={yOf(segment[0].midi as number, range)}
          r={1.5}
          fill={trailColor(segment[0].midi as number, range)}
          opacity={opacityOf(avgConfidence(segment))}
        />
      ) : (
        chunkForColor(segment).map((chunk, j) => (
          <polyline
            key={`${i}-${j}`}
            points={pointsAttr(chunk, mapX, range)}
            fill="none"
            stroke={trailColor(avgMidi(chunk), range)}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            opacity={opacityOf(avgConfidence(chunk))}
          />
        ))
      ),
    )}
  </>
);

/** 라이브 커서 — 현재 음 점 + "E3 +12¢" 라벨(레퍼런스 영상의 초록 마커). */
const LiveMarker = ({
  point,
  x,
  range,
}: {
  point: PitchGraphPoint;
  x: number;
  range: MidiRange;
}) => {
  const midi = point.midi as number;
  const y = yOf(midi, range);
  const cents = centsOff(midi);
  const label = `${noteLabel(midi)} ${cents >= 0 ? '+' : ''}${cents}¢`;
  const textY = Math.max(12, y - 10);
  return (
    <g>
      <circle
        cx={x}
        cy={y}
        r={4}
        fill="hsl(152 65% 45%)"
        stroke="hsl(var(--background))"
        strokeWidth={1.5}
      />
      <text
        x={Math.min(x - 8, VIEW_W - 60)}
        y={textY}
        textAnchor="end"
        fontSize={12}
        fontWeight={600}
        style={{
          fill: 'hsl(var(--foreground))',
          paintOrder: 'stroke',
          stroke: 'hsl(var(--background))',
          strokeWidth: 3,
        }}
      >
        {label}
      </text>
    </g>
  );
};

/**
 * 하단 Energy 스트립 — rms 막대, 유성 구간은 트레일과 같은 색.
 * 가운데를 축으로 위아래 대칭으로 그린다(레퍼런스의 파형 모양) — 소리의 세기가 커지고
 * 작아지는 것이 한쪽으로만 자랄 때보다 눈에 먼저 들어온다.
 */
const EnergyStrip = ({
  buckets,
  mapX,
  range,
}: {
  buckets: EnergyBucket[];
  mapX: (t: number) => number;
  range: MidiRange;
}) => (
  <svg
    viewBox={`0 0 ${VIEW_W} ${ENERGY_H}`}
    preserveAspectRatio="none"
    className="h-8 w-full select-none rounded-md border border-border bg-muted/20"
    role="img"
    aria-label="입력 에너지(rms)"
  >
    {buckets.map((b) => {
      const x = mapX(b.t0);
      const w = Math.max(1.5, mapX(b.t1) - x);
      const h = energyHeight(b.rmsDb);
      return (
        <rect
          key={b.t0}
          x={x}
          y={(ENERGY_H - h) / 2}
          width={w}
          height={h}
          fill={
            b.midi != null ? trailColor(b.midi, range) : 'hsl(var(--border))'
          }
          opacity={b.midi != null ? 0.85 : 0.5}
        />
      );
    })}
  </svg>
);

interface RealtimePitchGraphProps {
  /** median(3) 옥타브 글리치 스무딩 적용 여부(디버그 콘솔 토글). */
  smooth?: boolean;
}

/** 마지막 세그먼트의 끝점 — 라이브 마커가 앉을 자리(따라가기 중일 때만 표시). */
const lastDrawnPoint = (
  segments: PitchGraphPoint[][],
): PitchGraphPoint | null => {
  const tail = segments[segments.length - 1];
  return tail ? tail[tail.length - 1] : null;
};

export const RealtimePitchGraph = ({
  smooth = false,
}: RealtimePitchGraphProps = {}) => {
  const { points, sessionStart, sessionRange } = usePitchHistory();
  // 스크럽 한계 = 남아있는 가장 오래된 점(cap으로 잘린 범위), 시간축 라벨 = 진짜 세션 시작.
  // → 라벨은 실제 경과시간(mm:ss), 스크럽은 보유한 데이터까지.
  const oldestRetained = points.length ? points[0].t : sessionStart;
  const latest = points.length ? points[points.length - 1].t : 0;
  const { svgRef, viewEnd, autoFollow, goLive, pxWidth, handlers } =
    useViewportPan(latest, oldestRetained);

  const viewStart = viewEnd - VIEW_MS;
  const mapX = (t: number) => xInView(t, viewStart, VIEW_MS);
  // 표시 파이프라인: (median) → 스파이크 억제 → 잘라내기 → 세그먼트. 축 맞춤도 억제 후
  // 점을 써야 스파이크가 y축을 벌려놓지 않는다.
  //
  // 억제는 화면 밖 여유분까지 포함해 돌린다. 라이브로 따라갈 때 뷰포트 왼쪽 끝은 매 렌더
  // 다른 프레임에서 시작하는데, 판정이 그 첫 점에 의존하면 같은 구간이 렌더마다 다르게
  // 그려진다(시작점이 스파이크인 순간 필터가 뒤집힌다).
  const wide = sliceToView(
    points,
    viewStart,
    viewEnd,
    SPIKE_OCTAVE_HOLD_FRAMES + 2,
  );
  const display = trimToView(
    suppressOctaveSpikes(smooth ? medianSmoothMidi(wide) : wide),
    viewStart,
  );
  const segments = buildSegments(display);
  // y축은 보이는 목소리에 맞춰 부드럽게 따라간다(렌더당 15%씩 수렴 ≈ 0.5초).
  // 그릴 점이 없으면 마지막 범위를 유지 — 숨 쉴 때마다 축이 튀면 못 읽는다.
  const fitRef = useRef<MidiRange>({ ...FULL_RANGE });
  const fitTarget = fitMidiRange(display);
  if (fitTarget) {
    fitRef.current = {
      lo: fitRef.current.lo + (fitTarget.lo - fitRef.current.lo) * 0.15,
      hi: fitRef.current.hi + (fitTarget.hi - fitRef.current.hi) * 0.15,
    };
  }
  const range = fitRef.current;
  const ticks = buildTimeTicks(
    viewStart,
    viewEnd,
    sessionStart,
    VIEW_MS,
    Math.max(2, Math.round(pxWidth / 80)),
  );
  const livePoint = autoFollow ? lastDrawnPoint(segments) : null;

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-foreground">
          실시간 음정 그래프
        </span>
        <button
          type="button"
          onClick={goLive}
          aria-pressed={autoFollow}
          className={`flex items-center gap-1 rounded-md border px-3 py-1 text-xs font-medium transition motion-safe:active:scale-95 ${
            autoFollow
              ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-600'
              : 'border-border/60 text-muted-foreground hover:bg-muted'
          }`}
        >
          <span aria-hidden>●</span>
          LIVE
        </button>
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        className="h-48 w-full touch-none select-none rounded-md border border-border bg-muted/20"
        role="img"
        aria-label="실시간 음정 그래프 (좌우로 드래그하면 과거를 볼 수 있습니다)"
        onPointerDown={handlers.onPointerDown}
        onPointerMove={handlers.onPointerMove}
        onPointerUp={handlers.onPointerUp}
        onPointerCancel={handlers.onPointerUp}
        onPointerLeave={handlers.onPointerUp}
      >
        <PitchGridLines range={range} />

        {/* 세션 음역은 차트 안에 둔다 — 패널 헤더에 두면 '정지됨' 뱃지가 덮는다. */}
        {sessionRange && (
          <text
            x={4}
            y={13}
            fontSize={12}
            fontWeight={600}
            style={{
              fill: 'hsl(var(--foreground))',
              paintOrder: 'stroke',
              stroke: 'hsl(var(--background))',
              strokeWidth: 3,
            }}
          >
            {`Range ${noteLabel(sessionRange.lo)} → ${noteLabel(sessionRange.hi)}`}
          </text>
        )}

        {ticks.map((tick) => (
          <g key={tick.t}>
            <line
              x1={tick.x}
              y1={0}
              x2={tick.x}
              y2={PLOT_H}
              style={{ stroke: 'hsl(var(--border))' }}
              strokeWidth={1}
              strokeDasharray="2 4"
              vectorEffect="non-scaling-stroke"
            />
            <text
              x={tick.x + 3}
              y={VIEW_H - 4}
              style={{ fill: 'hsl(var(--muted-foreground))' }}
              fontSize={10}
            >
              {tick.label}
            </text>
          </g>
        ))}

        <PitchTrail segments={segments} mapX={mapX} range={range} />
        {livePoint && livePoint.midi != null && (
          <LiveMarker point={livePoint} x={mapX(livePoint.t)} range={range} />
        )}
      </svg>
      <div className="flex flex-col gap-1">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Energy
        </span>
        <EnergyStrip
          buckets={bucketEnergy(display)}
          mapX={mapX}
          range={range}
        />
      </div>
    </section>
  );
};
